/**
 * Conflict-of-interest check (利益冲突核查) types.
 *
 * Field shapes mirror the backend ConflictCheckResponse / ConflictCheckCreate
 * schemas (app/modules/jerry/schemas.py). The API layer camel-cases the JSON,
 * so interfaces here use camelCase. Status and party-role string values must
 * stay aligned with backend enums (app/modules/jerry/enums.py).
 */

/** Lifecycle of a conflict check (ConflictCheckStatus). */
export type ConflictCheckStatus =
  | "pending"
  | "in_review"
  | "cleared"
  | "conflict_found"
  | "waived";

/** Role a declared related party plays in a check (ConflictPartyRole). */
export type ConflictPartyRole =
  | "client"
  | "opposing_party"
  | "related_entity"
  | "affiliate"
  | "third_party"
  | "other";

/** Chinese display labels for conflict-check statuses (docs/glossary.md). */
export const CONFLICT_CHECK_STATUS_LABELS: Record<ConflictCheckStatus, string> = {
  pending: "待核查",
  in_review: "核查中",
  cleared: "已清结",
  conflict_found: "发现冲突",
  waived: "已豁免",
};

/** Chinese display labels for related-party roles. */
export const CONFLICT_PARTY_ROLE_LABELS: Record<ConflictPartyRole, string> = {
  client: "委托人",
  opposing_party: "对方当事人",
  related_entity: "关联方",
  affiliate: "关联企业",
  third_party: "第三方",
  other: "其他",
};

/** Selectable party-role options for the related-party form rows. */
export const CONFLICT_PARTY_ROLE_OPTIONS: { value: ConflictPartyRole; label: string }[] = (
  Object.entries(CONFLICT_PARTY_ROLE_LABELS) as [ConflictPartyRole, string][]
).map(([value, label]) => ({ value, label }));

/**
 * Allowed status transitions, mirrored from the backend state machine
 * (CONFLICT_CHECK_STATUS_TRANSITIONS). Terminal statuses map to empty lists.
 * Illegal transitions are never offered as a UI action.
 */
export const CONFLICT_CHECK_STATUS_TRANSITIONS: Record<
  ConflictCheckStatus,
  ConflictCheckStatus[]
> = {
  pending: ["in_review"],
  in_review: ["pending", "cleared", "conflict_found"],
  conflict_found: ["in_review", "waived"],
  cleared: [],
  waived: [],
};

/** Statuses that conclude a check; concluding requires a conclusion text. */
export const CONFLICT_CHECK_DECISION_STATUSES: ConflictCheckStatus[] = [
  "cleared",
  "conflict_found",
  "waived",
];

/** Terminal statuses — a concluded check is immutable (no further edits). */
export const CONFLICT_CHECK_TERMINAL_STATUSES: ConflictCheckStatus[] = [
  "cleared",
  "waived",
];

/** One hand-declared related party to screen (ConflictRelatedParty). */
export interface ConflictRelatedParty {
  name: string;
  role: ConflictPartyRole;
  identifier?: string | null;
  note?: string | null;
}

/** A conflict check record (ConflictCheckResponse). */
export interface ConflictCheck {
  id: string;
  tenantId: string;
  matterId: string | null;
  clientId: string | null;
  subject: string;
  status: ConflictCheckStatus;
  relatedParties: ConflictRelatedParty[];
  conclusion: string | null;
  createdBy: string;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Payload to raise a conflict check (ConflictCheckCreate). */
export interface ConflictCheckCreateRequest {
  matterId?: string;
  clientId?: string;
  subject: string;
  relatedParties: ConflictRelatedParty[];
}

/** Payload to edit a still-open check (ConflictCheckUpdate). */
export interface ConflictCheckUpdateRequest {
  subject?: string;
  relatedParties?: ConflictRelatedParty[];
}

/** Payload to advance a check's status (ConflictCheckStatusChange). */
export interface ConflictCheckStatusChangeRequest {
  status: ConflictCheckStatus;
  conclusion?: string;
}
